import { Search } from 'lucide-react'
import { useMemo } from 'react'

import { useCompanies } from '@/features/companies/api'
import { useProjectStages } from '@/features/projects/api'
import { UNASSIGNED } from '@/features/tasks/components'
import {
  Button,
  Input,
  MultiSelect,
  Segmented,
  Switch,
  type ListOption,
} from '@/shared/components/ui'
import { useAssignableUsers } from '@/shared/hooks/use-users'
import { useT } from '@/shared/i18n'
import { cn } from '@/shared/lib/cn'

import type { PortfolioScope, PortfolioView, UsePortfolioFilters } from '../use-portfolio-filters'

export interface PortfolioFiltersProps extends UsePortfolioFilters {
  /**
   * El alcance YA resuelto contra el rol.
   *
   * `filters.scope` puede ser `null` ("no lo dijo la URL"); esto nunca lo es.
   * La barra dibuja lo que se esta viendo, no lo que la URL alcanzo a decir.
   */
  scope: PortfolioScope
  /**
   * Si se dibuja el conmutador Tabla | Tarjetas | Tablero.
   *
   * La barra la comparten el portafolio y la linea de tiempo —para que "lo de
   * Nube Andina" signifique lo mismo en las dos— pero la vista es del
   * portafolio: en `/timeline` ese control ofrecia tres destinos que la
   * pantalla no tiene, y el que lo tocara no veria pasar nada.
   */
  showView?: boolean
  className?: string
}

/**
 * Barra sticky de filtros. TODO sale de la URL: lo que se ve es lo que se
 * comparte.
 */
export function PortfolioFiltersBar({
  filters,
  isFiltered,
  setFilter,
  clearFilters,
  scope,
  showView = true,
  className,
}: PortfolioFiltersProps) {
  const t = useT()
  const { data: stages = [] } = useProjectStages()
  const { data: companies = [], isPending: companiesPending } = useCompanies()
  const { data: users = [], isPending: usersPending } = useAssignableUsers()

  const companyOptions = useMemo<ListOption[]>(
    () =>
      companies.map((company) => ({
        value: company.id,
        label: company.name,
        text: company.name,
        // La espina de la empresa: el único lugar donde vive su color.
        leading: (
          <span
            aria-hidden="true"
            className="h-3.5 w-[var(--spine-w)] shrink-0 rounded-full"
            style={{ backgroundColor: `hsl(${String(company.color_hue)} 62% 55%)` }}
          />
        ),
      })),
    [companies],
  )

  // Las fases que lista el selector son las que cada empresa definio: sus
  // nombres viajan tal cual. Lo unico traducible es el "cualquiera".
  const stageOptions = useMemo<ListOption[]>(
    () => stages.map((stage) => ({ value: stage.id, label: stage.name, text: stage.name })),
    [stages],
  )

  const ownerOptions = useMemo<ListOption[]>(
    () => [
      { value: UNASSIGNED, label: t('common.label.noOwner'), text: t('common.label.noOwner') },
      ...users.map((user) => ({
        value: user.id,
        label: user.display_name,
        text: user.display_name,
      })),
    ],
    [users, t],
  )

  const scopes = [
    { value: 'mio', label: t('portfolio.filters.scopeMine') },
    { value: 'todo', label: t('portfolio.filters.scopeAll') },
  ] as const

  const views = [
    { value: 'tabla', label: t('portfolio.filters.viewTable') },
    { value: 'tarjetas', label: t('portfolio.filters.viewCards') },
    { value: 'tablero', label: t('portfolio.filters.viewBoard') },
  ] as const

  return (
    <div
      className={cn(
        'hairline-b sticky top-0 z-10 flex flex-wrap items-center gap-2 bg-surface-1 px-4 py-2',
        className,
      )}
    >
      {/*
        El alcance va PRIMERO: es la pregunta que enmarca a todas las demas.
        Filtrar por fase dentro de "lo mio" y dentro de "todo" son dos preguntas
        distintas, y leerlas en ese orden evita la tercera —"¿de quien es esto
        que estoy viendo?"— que es la que hoy no tenia respuesta.
      */}
      <Segmented
        label={t('portfolio.filters.scope')}
        value={scope}
        options={scopes}
        onChange={(value: PortfolioScope) => setFilter('scope', value)}
      />

      <MultiSelect
        label={t('common.label.company')}
        placeholder={t('companies.switcher.all')}
        options={companyOptions}
        values={filters.companies}
        disabled={companiesPending}
        onChange={(ids) => setFilter('companies', ids)}
      />

      <MultiSelect
        label={t('common.label.phase')}
        placeholder={t('portfolio.filters.anyPhase')}
        options={stageOptions}
        values={filters.stages}
        onChange={(ids) => setFilter('stages', ids)}
      />

      {/*
        En "solo lo mio" el responsable YA esta decidido: mostrar el selector
        seria ofrecer dos controles para la misma dimension, y el que perdiera
        dejaria la pantalla diciendo una cosa y filtrando otra.
      */}
      {scope === 'todo' ? (
        <MultiSelect
          label={t('common.label.owner')}
          placeholder={t('tasks.assignee.any')}
          options={ownerOptions}
          values={filters.owners}
          disabled={usersPending}
          onChange={(ids) => setFilter('owners', ids)}
        />
      ) : null}

      <Switch
        label={t('portfolio.filters.includeOngoing')}
        checked={filters.includeOngoing}
        onChange={(checked) => setFilter('includeOngoing', checked)}
      />

      <label className="relative flex min-w-[180px] flex-1 items-center">
        <Search
          size={13}
          aria-hidden="true"
          className="pointer-events-none absolute left-2 text-text-3"
        />
        <Input
          type="search"
          aria-label={t('portfolio.filters.searchLabel')}
          placeholder={t('portfolio.filters.searchPlaceholder')}
          className="pl-7"
          value={filters.search}
          onChange={(event) => setFilter('search', event.target.value)}
        />
      </label>

      {isFiltered ? (
        <Button variant="ghost" onClick={clearFilters}>
          {t('common.action.clearFilters')}
        </Button>
      ) : null}

      {showView ? (
        <Segmented
          label={t('portfolio.filters.view')}
          value={filters.view}
          options={views}
          onChange={(view: PortfolioView) => setFilter('view', view)}
        />
      ) : null}
    </div>
  )
}
